import Button from 'components/base/Button';
import AuthSimpleLayout from 'layouts/AuthSimpleLayout';
import { Form } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import { FormEvent, useState } from 'react';
import { login } from 'features/auth/authApi';
import { UserRole } from 'features/auth/authSession';
import { useAuth } from 'providers/AuthProvider';

const LoginPage = () => {
  const navigate = useNavigate();
  const { login: loginWithSession } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [selectedRole, setSelectedRole] = useState<UserRole>('locum');
  const [errorMessage, setErrorMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setErrorMessage('');
    setIsLoading(true);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/auth/login`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email, password })
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Login failed');
      }

      const data = await response.json();
      const session = { token: data.token, user: data.user };

      // Verify role matches
      if (session.user.role !== selectedRole) {
        setErrorMessage('Invalid credentials');
        return;
      }

      loginWithSession(session);
      navigate('/dashboard', { replace: true });
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'Login failed');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthSimpleLayout>
      <div className="text-center mb-6">
        <h3 className="text-body-highlight">Login</h3>
      </div>

      <Form onSubmit={handleSubmit}>
        <Form.Group className="mb-3 text-start">
          <Form.Label className="d-block mb-2">Login as</Form.Label>
          <Form.Check
            inline
            type="radio"
            id="role-locum"
            name="login-role"
            label="Locum"
            checked={selectedRole === 'locum'}
            onChange={() => setSelectedRole('locum')}
          />
          <Form.Check
            inline
            type="radio"
            id="role-pharmacy"
            name="login-role"
            label="Pharmacy"
            checked={selectedRole === 'pharmacy'}
            onChange={() => setSelectedRole('pharmacy')}
          />
        </Form.Group>

        <Form.Group className="mb-3 text-start">
          <Form.Label>Email</Form.Label>
          <Form.Control
            type="email"
            required
            value={email}
            onChange={e => setEmail(e.target.value)}
            placeholder="name@example.com"
          />
        </Form.Group>

        <Form.Group className="mb-3 text-start">
          <Form.Label>Password</Form.Label>
          <Form.Control
            type="password"
            required
            value={password}
            onChange={e => setPassword(e.target.value)}
            placeholder="Password"
          />
        </Form.Group>

        {errorMessage && (
          <div className="alert alert-danger py-2" role="alert">
            {errorMessage}
          </div>
        )}

        <Button
          type="submit"
          variant="primary"
          className="w-100 mb-3"
          disabled={isLoading}
        >
          {isLoading ? 'Logging in...' : 'Login'}
        </Button>

        <div className="text-center fs-9">
          New here? <Link to="/auth/register">Create account</Link>
          <div className="mt-2">
            <Link to="/auth/admin-login">Admin Login</Link>
          </div>
        </div>
      </Form>
    </AuthSimpleLayout>
  );
};

export default LoginPage;
